import React, { useState, useRef, useEffect } from 'react';
import { Icons } from './Icons';
import { useTree, useTreePeople } from '../contexts/TreeContext';
import { formatDate, getAge, getInitials, ordinal, deriveMarriages } from '../utils/helpers';
import { useSignedUrl, usePersonPhotoUrl, useImageUrl } from '../utils/signedUrls';
import AddRelatedPersonModal from './AddRelatedPersonModal';
import { useAuth } from '../contexts/AuthContext';
import { VisibilityPicker, shareableMembers, describeVisibility } from './AttachmentVisibility';

const EVENT_COLORS = [
  { value: '#6DAE72', label: 'Life' },
  { value: '#C4975A', label: 'Career' },
  { value: '#8E6CC0', label: 'Family' },
  { value: '#5B8AC4', label: 'Education' },
  { value: '#C0392B', label: 'Loss' },
  { value: '#8B7D6B', label: 'Other' },
];

export default function PersonDetailPanel({ personId, onClose, onEditRelationships, canEdit, onUnsavedChange, isMobile }) {
  const { uploadAttachment, deleteAttachment, setAttachmentVisibility, deletePerson,
          uploadPersonPhoto, deletePersonPhoto, updatePerson,
          treeMembers, memberDetails, userRole } = useTree();
  const { people, attachments } = useTreePeople();
  const { user } = useAuth();
  const selfUid = user?.uid;
  const isOwner = userRole === 'owner';
  const members = shareableMembers(treeMembers, memberDetails, selfUid);

  const [tab, setTab] = useState('details');
  const [uploading, setUploading] = useState(false);
  // Who the next upload is visible to; null is everyone in the tree.
  const [uploadVisibility, setUploadVisibility] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [avatarHover, setAvatarHover] = useState(false);
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);

  // Details editing
  const [editingDetails, setEditingDetails] = useState(false);
  const [detailsDraft, setDetailsDraft] = useState(null);
  const [savingDetails, setSavingDetails] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Timeline event editing
  const [editingEventId, setEditingEventId] = useState(null); // 'new' or event id
  const [eventDraft, setEventDraft] = useState({ year: '', text: '', color: '#6DAE72' });

  // Add related person modal: { mode: 'parent'|'child'|'spouse', secondParentId?: string } | null
  const [addRelationConfig, setAddRelationConfig] = useState(null);

  // Reset all edit state when the selected person changes
  useEffect(() => {
    setEditingDetails(false);
    setDetailsDraft(null);
    setSavingDetails(false);
    setEditingEventId(null);
    setEventDraft({ year: '', text: '', color: '#6DAE72' });
    setAddRelationConfig(null);
    // Back to the default on a new person, so a private choice made for one
    // person's papers does not silently carry over to the next.
    setUploadVisibility(null);
  }, [personId]);

  const person = people[personId];
  // 64px header avatar — the largest photo the app renders, so on a 3x display
  // this is the one call that reaches for the 512px rendition.
  const photoUrl = usePersonPhotoUrl(person, 64);
  if (!person) return null;

  const alive = !person.death;
  const age = getAge(person.birth, person.death);
  const atts = attachments[personId] || [];

  // Derive children from parentIds (more reliable than stored children array)
  const derivedChildIds = Object.values(people)
    .filter(p => p.parentIds && p.parentIds.includes(personId))
    .sort((a, b) => (parseInt(a.birth) || 9999) - (parseInt(b.birth) || 9999))
    .map(p => p.id);

  // Own marriages plus any recorded only on the other spouse's document.
  // Shared with EditRelationshipsModal so the two never disagree about who a
  // person is married to.
  const derivedMarriages = deriveMarriages(personId, people);

  // ── Handlers ──

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) await uploadAttachment(personId, f, uploadVisibility);
    } catch (err) {
      const msg = err?.code === 'storage/unauthorized'
        ? 'Upload blocked: file type or size not permitted.'
        : `Upload failed: ${err?.message || 'unknown error'}`;
      alert(msg);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoUploading(true);
    try {
      // The server cleans up the photo this one replaces, on finalize.
      await uploadPersonPhoto(personId, file);
    } catch (err) {
      // Without this the promise rejected unhandled and the upload simply
      // appeared to do nothing — the attachment path next to it has always
      // surfaced failures, and this one should too.
      alert(`Photo upload failed: ${err?.message || 'unknown error'}`);
    } finally {
      setPhotoUploading(false);
      e.target.value = '';
    }
  };

  const handleRemovePhoto = async (e) => {
    e.stopPropagation();
    await deletePersonPhoto(personId);
  };

  // Guarded against a second click while the first delete is still in flight.
  // The sweep now runs server-side and can take a moment on a large tree, and a
  // repeat request would fail with "person not found" rather than no-op'ing the
  // way the old client-side batch delete did.
  const handleDelete = async () => {
    if (deleting) return;
    if (!window.confirm(`Remove ${person.name} from the tree? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await deletePerson(personId);
      onClose();
    } finally {
      setDeleting(false);
    }
  };

  const startEditDetails = () => {
    const parts = person.name.trim().split(' ');
    const firstName = parts[0] || '';
    const lastName = parts.slice(1).join(' ');
    setDetailsDraft({
      firstName,
      lastName,
      gender: person.gender || 'M',
      birth: person.birth || '',
      birthPlace: person.birthPlace || '',
      death: person.death || '',
      deathPlace: person.deathPlace || '',
      occupation: person.occupation || '',
      notes: person.notes || '',
    });
    setEditingDetails(true);
    onUnsavedChange?.(true);
  };

  const cancelEditDetails = () => {
    setEditingDetails(false);
    onUnsavedChange?.(false);
  };

  const saveDetails = async () => {
    const name = `${detailsDraft.firstName.trim()} ${detailsDraft.lastName.trim()}`.trim();
    if (!name) return;
    setSavingDetails(true);
    await updatePerson(personId, {
      name,
      gender: detailsDraft.gender,
      birth: detailsDraft.birth || null,
      birthPlace: detailsDraft.birthPlace || null,
      death: detailsDraft.death || null,
      deathPlace: detailsDraft.deathPlace || null,
      occupation: detailsDraft.occupation || null,
      notes: detailsDraft.notes || null,
    });
    setSavingDetails(false);
    setEditingDetails(false);
    onUnsavedChange?.(false);
  };

  const startAddEvent = () => {
    setEventDraft({ year: '', text: '', color: '#6DAE72' });
    setEditingEventId('new');
    onUnsavedChange?.(true);
  };

  const startEditEvent = (ev) => {
    setEventDraft({ year: ev.year, text: ev.text, color: ev.color });
    setEditingEventId(ev.id);
    onUnsavedChange?.(true);
  };

  const cancelEditEvent = () => {
    setEditingEventId(null);
    onUnsavedChange?.(false);
  };

  const saveEvent = async () => {
    if (!eventDraft.year.trim() || !eventDraft.text.trim()) return;
    const events = [...(person.lifeEvents || [])];
    if (editingEventId === 'new') {
      events.push({ id: crypto.randomUUID(), year: eventDraft.year.trim(), text: eventDraft.text.trim(), color: eventDraft.color });
    } else {
      const idx = events.findIndex(e => e.id === editingEventId);
      if (idx >= 0) events[idx] = { ...events[idx], year: eventDraft.year.trim(), text: eventDraft.text.trim(), color: eventDraft.color };
    }
    await updatePerson(personId, { lifeEvents: events });
    setEditingEventId(null);
    onUnsavedChange?.(false);
  };

  const deleteEvent = async (eventId) => {
    const events = (person.lifeEvents || []).filter(e => e.id !== eventId);
    await updatePerson(personId, { lifeEvents: events });
  };

  // ── Build sorted timeline ──
  const buildTimeline = () => {
    const items = [];
    if (person.birth) {
      items.push({ key: 'birth', year: person.birth.slice(0, 4), text: `Born in ${person.birthPlace || 'unknown location'}`, color: '#6DAE72', auto: true });
    }
    // Read from both sides, like the Relationships tab and the canvas already
    // do: a marriage stored only on the other spouse's document used to appear
    // on their timeline and be missing from this one. Spouse-less entries are
    // added back because deriveMarriages drops them and a bare marriage date is
    // still worth a row.
    const spouselessOwn = (person.marriages || []).filter(m => !m.spouseId && m.marriageDate);
    [...derivedMarriages, ...spouselessOwn].forEach((m, i) => {
      items.push({ key: `m${i}`, year: m.marriageDate?.slice(0, 4) || '?', text: `Married ${people[m.spouseId]?.name || 'unknown'}${m.marriagePlace ? ` in ${m.marriagePlace}` : ''}`, color: '#8E6CC0', auto: true });
    });
    (person.lifeEvents || []).forEach(ev => {
      items.push({ ...ev, key: ev.id, auto: false });
    });
    if (person.death) {
      items.push({ key: 'death', year: person.death.slice(0, 4), text: `Passed away in ${person.deathPlace || 'unknown location'}`, color: '#B0A090', auto: true });
    }
    return items.sort((a, b) => {
      const ay = parseInt(a.year) || 0;
      const by = parseInt(b.year) || 0;
      return ay - by;
    });
  };

  const timeline = buildTimeline();

  return (
    <div style={{
      width: isMobile ? '100%' : 420, background: '#FDFBF7',
      ...(isMobile ? {} : { borderLeft: '1px solid #E8E0D4', animation: 'slideIn 0.25s ease-out' }),
      display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans', sans-serif",
      ...(isMobile ? { flex: 1, minHeight: 0 } : { height: '100%' }),
    }}>
      {/* Header */}
      <div style={{
        padding: '28px 24px 20px', borderBottom: '1px solid #E8E0D4',
        background: 'linear-gradient(180deg, #F5EDE3 0%, #FDFBF7 100%)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            {/* Clickable avatar */}
            {canEdit && <input type="file" ref={photoInputRef} onChange={handlePhotoUpload} accept="image/*" style={{ display: 'none' }} />}
            <div
              style={{ position: 'relative', cursor: canEdit ? 'pointer' : 'default', flexShrink: 0 }}
              onClick={() => canEdit && photoInputRef.current?.click()}
              onMouseEnter={() => canEdit && setAvatarHover(true)}
              onMouseLeave={() => setAvatarHover(false)}
              title={canEdit ? (photoUrl ? 'Change photo' : 'Add photo') : undefined}
            >
              <div style={{
                width: 64, height: 64, borderRadius: '50%',
                background: person.gender === 'F' ? '#DBC8CE' : '#C8CEDB',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20, fontWeight: 700, color: '#6B5E4F',
                fontFamily: "'Crimson Pro', Georgia, serif",
                border: '3px solid #fff', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                overflow: 'hidden',
              }}>
                {photoUploading
                  ? <div style={{ fontSize: 10, color: '#C4975A', fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>...</div>
                  : photoUrl
                    ? <img src={photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : getInitials(person.name)
                }
              </div>
              {canEdit && avatarHover && !photoUploading && (
                <div style={{
                  position: 'absolute', inset: 0, borderRadius: '50%',
                  background: 'rgba(0,0,0,0.45)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                }}>
                  <Icons.Camera size={18} />
                </div>
              )}
              {canEdit && photoUrl && !avatarHover && !photoUploading && (
                <button onClick={handleRemovePhoto} title="Remove photo" style={{
                  position: 'absolute', top: -4, right: -4,
                  width: 20, height: 20, borderRadius: '50%',
                  background: '#C0392B', border: '2px solid #fff',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', padding: 0,
                }}>
                  <Icons.Close size={8} />
                </button>
              )}
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 20, fontFamily: "'Crimson Pro', Georgia, serif", color: '#2C2418', fontWeight: 700, lineHeight: 1.2 }}>
                {person.name}
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                {alive
                  ? <span style={{ fontSize: 11, background: '#E8F5E9', color: '#4A7C4F', padding: '2px 10px', borderRadius: 20, fontWeight: 600 }}>Living{age !== null ? ` · Age ${age}` : ''}</span>
                  : <span style={{ fontSize: 11, background: '#F0EBE3', color: '#8B7D6B', padding: '2px 10px', borderRadius: 20, fontWeight: 600 }}>Deceased{age !== null ? ` · Lived to ${age}` : ''}</span>
                }
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8B7D6B', padding: 4 }}>
            <Icons.Close />
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
          {canEdit ? (
            <button onClick={handleDelete} disabled={deleting} style={{
              padding: '9px 16px', background: 'none', border: '1.5px solid #E8E0D4',
              borderRadius: 8, fontSize: 12, fontWeight: 600,
              cursor: deleting ? 'default' : 'pointer', opacity: deleting ? 0.6 : 1,
              display: 'flex', alignItems: 'center', gap: 5, color: '#C0392B',
              fontFamily: "'DM Sans', sans-serif",
            }}>
              <Icons.Trash /> {deleting ? 'Removing…' : 'Remove'}
            </button>
          ) : <span />}
          <img
            src="/kt_logo_trans.png"
            alt="Kinship Trees"
            style={{ height: 64, width: 'auto', opacity: 0.75 }}
          />
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #E8E0D4', padding: '0 24px' }}>
        {['details', 'attachments', 'relationships'].map(t => (
          <button key={t} onClick={() => { setTab(t); setEditingDetails(false); setEditingEventId(null); onUnsavedChange?.(false); }} style={{
            background: 'none', border: 'none', padding: '12px 16px',
            fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
            color: tab === t ? '#C4975A' : '#8B7D6B',
            borderBottom: tab === t ? '2px solid #C4975A' : '2px solid transparent',
            textTransform: 'capitalize', fontFamily: "'DM Sans', sans-serif",
          }}>{t}{t === 'attachments' && atts.length > 0 ? ` (${atts.length})` : ''}</button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>

        {/* ── Details tab ── */}
        {tab === 'details' && !editingDetails && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Edit button */}
            {canEdit && (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={startEditDetails} style={{
                  padding: '7px 14px', background: 'rgba(196,151,90,0.12)', border: 'none',
                  borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  color: '#C4975A', fontFamily: "'DM Sans', sans-serif",
                  display: 'flex', alignItems: 'center', gap: 5,
                }}>
                  <Icons.Edit /> Edit Details
                </button>
              </div>
            )}

            {person.birth && <DetailRow icon={<Icons.Calendar />} label="Born" value={formatDate(person.birth)} sub={person.birthPlace} />}
            {person.death && <DetailRow icon={<Icons.Calendar />} label="Died" value={formatDate(person.death)} sub={person.deathPlace} />}
            {person.birthPlace && !person.birth && <DetailRow icon={<Icons.MapPin />} label="Birth Place" value={person.birthPlace} />}
            {person.deathPlace && !person.death && <DetailRow icon={<Icons.MapPin />} label="Death Place" value={person.deathPlace} />}
            {person.occupation && <DetailRow icon={<Icons.Briefcase />} label="Occupation" value={person.occupation} />}

            {/* Notes */}
            {person.notes && (
              <div>
                <h4 style={{ ...sectionHeader, marginBottom: 8 }}>About</h4>
                <p style={{
                  margin: 0, fontSize: 13, color: '#4A3F33', lineHeight: 1.7,
                  background: '#FAF7F2', borderRadius: 10, padding: '12px 14px',
                  border: '1px solid #EBE4D8', whiteSpace: 'pre-wrap',
                  fontFamily: "'Crimson Pro', Georgia, serif",
                }}>{person.notes}</p>
              </div>
            )}

            {/* Timeline */}
            <div style={{ marginTop: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <h4 style={{ ...sectionHeader, margin: 0 }}>Life Timeline</h4>
                {canEdit && (
                  <button onClick={startAddEvent} style={{
                    padding: '5px 10px', background: 'rgba(196,151,90,0.12)', border: 'none',
                    borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    color: '#C4975A', fontFamily: "'DM Sans', sans-serif",
                    display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                    <Icons.Plus size={10} /> Add Event
                  </button>
                )}
              </div>

              <div style={{ borderLeft: '2px solid #E8E0D4', paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
                {timeline.map(item => (
                  editingEventId === item.key ? (
                    <EventForm
                      key={item.key}
                      draft={eventDraft}
                      onChange={setEventDraft}
                      onSave={saveEvent}
                      onCancel={cancelEditEvent}
                    />
                  ) : (
                    <TimelineItem
                      key={item.key}
                      year={item.year}
                      text={item.text}
                      color={item.color}
                      editable={canEdit && !item.auto}
                      onEdit={() => startEditEvent(item)}
                      onDelete={() => deleteEvent(item.id)}
                    />
                  )
                ))}

                {/* Add event form */}
                {editingEventId === 'new' && (
                  <EventForm
                    draft={eventDraft}
                    onChange={setEventDraft}
                    onSave={saveEvent}
                    onCancel={cancelEditEvent}
                  />
                )}

                {timeline.length === 0 && editingEventId !== 'new' && (
                  <p style={{ margin: 0, fontSize: 12, color: '#A0937F' }}>No events recorded yet</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Details edit form ── */}
        {tab === 'details' && editingDetails && detailsDraft && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <h4 style={{ ...sectionHeader, margin: '0 0 4px' }}>Edit Details</h4>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <EditField label="First Name" value={detailsDraft.firstName} onChange={v => setDetailsDraft(d => ({ ...d, firstName: v }))} />
              <EditField label="Last Name" value={detailsDraft.lastName} onChange={v => setDetailsDraft(d => ({ ...d, lastName: v }))} />
              <EditField label="Date of Birth" value={detailsDraft.birth} onChange={v => setDetailsDraft(d => ({ ...d, birth: v }))} placeholder="YYYY-MM-DD" />
              <EditField label="Date of Death" value={detailsDraft.death} onChange={v => setDetailsDraft(d => ({ ...d, death: v }))} placeholder="Leave empty if living" />
              <EditField label="Birth Place" value={detailsDraft.birthPlace} onChange={v => setDetailsDraft(d => ({ ...d, birthPlace: v }))} placeholder="City, Country" />
              <EditField label="Death Place" value={detailsDraft.deathPlace} onChange={v => setDetailsDraft(d => ({ ...d, deathPlace: v }))} placeholder="City, Country" />
              <div style={{ gridColumn: 'span 2' }}>
                <EditField label="Occupation" value={detailsDraft.occupation} onChange={v => setDetailsDraft(d => ({ ...d, occupation: v }))} placeholder="e.g. Teacher" />
              </div>

              {/* Notes */}
              <div style={{ gridColumn: 'span 2' }}>
                <label style={labelStyle}>About</label>
                <textarea
                  value={detailsDraft.notes}
                  onChange={e => setDetailsDraft(d => ({ ...d, notes: e.target.value }))}
                  placeholder="Write anything about this person — personality, memories, stories…"
                  rows={5}
                  style={{
                    ...inputStyle, resize: 'vertical', lineHeight: 1.6,
                    fontFamily: "'Crimson Pro', Georgia, serif", fontSize: 14,
                  }}
                />
              </div>

              {/* Gender */}
              <div style={{ gridColumn: 'span 2' }}>
                <label style={labelStyle}>Gender</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[['M', 'Male'], ['F', 'Female'], ['O', 'Other']].map(([v, l]) => (
                    <button key={v} onClick={() => setDetailsDraft(d => ({ ...d, gender: v }))} style={{
                      flex: 1, padding: '9px 0', borderRadius: 8,
                      border: detailsDraft.gender === v ? '2px solid #C4975A' : '1.5px solid #D5CAB8',
                      background: detailsDraft.gender === v ? '#F5EDE3' : '#fff',
                      color: detailsDraft.gender === v ? '#C4975A' : '#6B5E4F',
                      fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      fontFamily: "'DM Sans', sans-serif",
                    }}>{l}</button>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button onClick={cancelEditDetails} style={{
                flex: 1, padding: '10px 0', borderRadius: 8, border: '1.5px solid #D5CAB8',
                background: '#fff', color: '#6B5E4F', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
              }}>Cancel</button>
              <button onClick={saveDetails} disabled={savingDetails} style={{
                flex: 2, padding: '10px 0', borderRadius: 8, border: 'none',
                background: savingDetails ? '#B8AFA0' : '#3B2F1E',
                color: '#F5EDE3', fontSize: 13, fontWeight: 600,
                cursor: savingDetails ? 'wait' : 'pointer', fontFamily: "'DM Sans', sans-serif",
              }}>{savingDetails ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </div>
        )}

        {/* ── Attachments tab ── */}
        {tab === 'attachments' && (
          <div>
            {canEdit && (
              <>
                <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" multiple style={{ display: 'none' }} />
                <div style={{ marginBottom: 10 }}>
                  <p style={{ ...labelStyle, marginBottom: 7 }}>Who can see new uploads</p>
                  <VisibilityPicker value={uploadVisibility} onChange={setUploadVisibility}
                    members={members} isOwner={isOwner} />
                </div>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    border: '2px dashed #D5CAB8', borderRadius: 12, padding: '28px 20px',
                    textAlign: 'center', marginBottom: 20, background: '#FAF7F2',
                    cursor: 'pointer',
                  }}
                >
                  {uploading ? (
                    <p style={{ margin: 0, fontSize: 13, color: '#C4975A', fontWeight: 600 }}>Uploading...</p>
                  ) : (
                    <>
                      <div style={{ color: '#C4975A', marginBottom: 8 }}><Icons.Upload /></div>
                      <p style={{ margin: 0, fontSize: 13, color: '#6B5E4F', fontWeight: 600 }}>Drop files here or click to upload</p>
                      <p style={{ margin: '4px 0 0', fontSize: 11, color: '#A0937F' }}>Videos up to 100MB each and Photos, Audio, Documents (PDF, Word, Excel, CSV, TXT) up to 10MB each</p>
                      {uploadVisibility !== null && (
                        <p style={{ margin: '10px 0 0', fontSize: 12, color: '#8A6530', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                          <Icons.Lock size={11} />
                          {uploadVisibility.length === 0
                            ? 'Only you will see these files'
                            : `Only you and ${uploadVisibility.length} ${uploadVisibility.length === 1 ? 'person' : 'people'} you chose will see these files`}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </>
            )}

            {atts.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {atts.map(a => (
                  <AttachmentRow key={a.id} a={a} canEdit={canEdit}
                    onDelete={() => deleteAttachment(a.id, a.storagePath, a.size)}
                    visibility={describeVisibility(a, selfUid, memberDetails)}
                    canManageVisibility={isOwner || (a.uploadedBy && a.uploadedBy === selfUid)}
                    members={members} isOwner={isOwner}
                    onSetVisibility={(visibleTo) => setAttachmentVisibility(a.id, visibleTo)} />
                ))}
              </div>
            ) : (
              <p style={{ textAlign: 'center', color: '#A0937F', fontSize: 13, marginTop: 20 }}>No attachments yet</p>
            )}
          </div>
        )}

        {/* ── Relationships tab ── */}
        {tab === 'relationships' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {canEdit && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {[
                  { mode: 'parent', label: 'Add Parent', hidden: (person.parentIds || []).length >= 2 },
                  { mode: 'child',  label: 'Add Child',  hidden: false },
                  { mode: 'spouse', label: 'Add Spouse', hidden: derivedMarriages.some(m => m.status === 'current') },
                ].filter(({ hidden }) => !hidden).map(({ mode, label }) => (
                  <button key={mode} onClick={() => setAddRelationConfig({ mode })} style={{
                    padding: '7px 13px', background: 'rgba(59,47,30,0.07)', border: 'none',
                    borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    color: '#3B2F1E', fontFamily: "'DM Sans', sans-serif",
                    display: 'flex', alignItems: 'center', gap: 5,
                  }}>
                    <Icons.Plus size={11} /> {label}
                  </button>
                ))}
                <button onClick={onEditRelationships} style={{
                  padding: '7px 13px', background: 'rgba(196,151,90,0.12)', border: 'none',
                  borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  color: '#C4975A', fontFamily: "'DM Sans', sans-serif",
                  display: 'flex', alignItems: 'center', gap: 5,
                }}>
                  <Icons.Edit size={11} /> Edit
                </button>
              </div>
            )}

            {/* Marriages */}
            <div>
              <h4 style={{ ...sectionHeader, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icons.Ring /> Marriages & Partnerships
                <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: '#C4975A', background: 'rgba(196,151,90,0.12)', padding: '2px 8px', borderRadius: 10 }}>
                  {derivedMarriages.length || 0}
                </span>
              </h4>

              {derivedMarriages.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {derivedMarriages.map((m, idx) => {
                    const spouse = people[m.spouseId];
                    const isCurrent = m.status === 'current' && !spouse?.death && !person.death;
                    const endLabels = { divorce: 'Divorced', death: 'Ended by death of spouse', annulment: 'Annulled', separation: 'Separated' };
                    const endColors = {
                      divorce: { bg: '#FFF3E0', text: '#BF6C00', border: '#FFD89E' },
                      death: { bg: '#F0EBE3', text: '#8B7D6B', border: '#DDD5C8' },
                      annulment: { bg: '#FDE8E8', text: '#C0392B', border: '#F5C6C6' },
                      separation: { bg: '#FFF8E1', text: '#9A7D0A', border: '#F0E4A0' },
                    };
                    const ec = endColors[m.endReason] || endColors.death;

                    return (
                      <div key={idx} style={{
                        background: isCurrent ? '#FDFBF7' : '#FAF7F2',
                        borderRadius: 12, border: isCurrent ? '2px solid #C4975A' : '1px solid #EBE4D8',
                        overflow: 'hidden',
                      }}>
                        <div style={{ padding: '10px 14px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2, color: isCurrent ? '#C4975A' : '#A0937F' }}>
                            {derivedMarriages.length > 1 ? `${ordinal(idx + 1)} Marriage` : 'Marriage'}
                          </span>
                          {isCurrent
                            ? <span style={{ fontSize: 10, fontWeight: 700, color: '#4A7C4F', background: '#E8F5E9', padding: '2px 10px', borderRadius: 20, display: 'flex', alignItems: 'center', gap: 4 }}><Icons.Heart /> Current</span>
                            : <span style={{ fontSize: 10, fontWeight: 700, color: ec.text, background: ec.bg, padding: '2px 10px', borderRadius: 20, border: `1px solid ${ec.border}` }}>{endLabels[m.endReason] || 'Ended'}</span>
                          }
                        </div>

                        {spouse && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px' }}>
                            <PersonAvatar person={spouse} size={40}
                              borderColor={isCurrent ? '#C4975A' : '#DDD5C8'} />
                            <div style={{ flex: 1 }}>
                              <p style={{ margin: 0, fontSize: 13.5, fontWeight: 600, color: '#2C2418' }}>{spouse.name}</p>
                              <p style={{ margin: '2px 0 0', fontSize: 11, color: '#A0937F' }}>
                                {spouse.birth?.slice(0, 4)}–{spouse.death ? spouse.death.slice(0, 4) : 'present'}
                                {spouse.occupation && ` · ${spouse.occupation}`}
                              </p>
                            </div>
                          </div>
                        )}

                        <div style={{ padding: '8px 14px 12px', display: 'flex', flexWrap: 'wrap', gap: 12, borderTop: '1px solid #EBE4D8', marginTop: 2 }}>
                          {m.marriageDate && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                              <Icons.Ring />
                              <div>
                                <p style={{ margin: 0, fontSize: 10, color: '#A0937F', fontWeight: 600 }}>MARRIED</p>
                                <p style={{ margin: 0, fontSize: 12, color: '#2C2418', fontWeight: 500 }}>{formatDate(m.marriageDate)}</p>
                              </div>
                            </div>
                          )}
                          {m.marriagePlace && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                              <Icons.MapPin />
                              <div>
                                <p style={{ margin: 0, fontSize: 10, color: '#A0937F', fontWeight: 600 }}>PLACE</p>
                                <p style={{ margin: 0, fontSize: 12, color: '#2C2418', fontWeight: 500 }}>{m.marriagePlace}</p>
                              </div>
                            </div>
                          )}
                          {!isCurrent && m.endDate && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                              <Icons.Unlink />
                              <div>
                                <p style={{ margin: 0, fontSize: 10, color: ec.text, fontWeight: 600 }}>
                                  {m.endReason === 'divorce' ? 'DIVORCED' : m.endReason === 'annulment' ? 'ANNULLED' : 'ENDED'}
                                </p>
                                <p style={{ margin: 0, fontSize: 12, color: '#2C2418', fontWeight: 500 }}>{formatDate(m.endDate)}</p>
                              </div>
                            </div>
                          )}
                        </div>

                        {m.children?.length > 0 && (
                          <div style={{ padding: '8px 14px 12px', borderTop: '1px solid #EBE4D8' }}>
                            <p style={{ margin: '0 0 6px', fontSize: 10, color: '#A0937F', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                              Children from this marriage
                            </p>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              {m.children.map(cid => {
                                const child = people[cid];
                                if (!child) return null;
                                return (
                                  <div key={cid} style={{
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    padding: '5px 10px', background: '#fff', borderRadius: 8,
                                    border: '1px solid #EBE4D8', fontSize: 12,
                                  }}>
                                    <div style={{
                                      width: 22, height: 22, borderRadius: '50%',
                                      background: child.gender === 'F' ? '#E8D5C0' : '#C8CEDB',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      fontSize: 9, fontWeight: 700, color: '#6B5E4F',
                                      fontFamily: "'Crimson Pro', Georgia, serif",
                                    }}>{getInitials(child.name)}</div>
                                    <span style={{ fontWeight: 500, color: '#2C2418' }}>{child.name.split(' ')[0]}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {canEdit && m.spouseId && (
                          <div style={{ padding: '8px 14px 10px', borderTop: '1px solid #EBE4D8' }}>
                            <button
                              onClick={() => setAddRelationConfig({ mode: 'child', secondParentId: m.spouseId })}
                              style={{
                                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                                fontSize: 12, fontWeight: 600, color: '#C4975A',
                                fontFamily: "'DM Sans', sans-serif",
                                display: 'flex', alignItems: 'center', gap: 4,
                              }}
                            >
                              <Icons.Plus size={10} /> Add child to this marriage
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ fontSize: 13, color: '#A0937F', textAlign: 'center', padding: 16 }}>No marriages recorded</p>
              )}
            </div>

            {derivedChildIds.length > 0 && <RelationGroup label="All Children" ids={derivedChildIds} people={people} />}
            {person.parentIds?.length > 0 && <RelationGroup label="Parents" ids={person.parentIds} people={people} />}
          </div>
        )}
      </div>

      {addRelationConfig && (
        <AddRelatedPersonModal
          relationshipType={addRelationConfig.mode}
          anchorPerson={person}
          secondParentId={addRelationConfig.secondParentId}
          onClose={() => setAddRelationConfig(null)}
        />
      )}
    </div>
  );
}

// ── Sub-components ──

function DetailRow({ icon, label, value, sub }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <div style={{ color: '#C4975A', marginTop: 2 }}>{icon}</div>
      <div>
        <p style={{ margin: 0, fontSize: 11, color: '#A0937F', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>{label}</p>
        <p style={{ margin: '3px 0 0', fontSize: 14, color: '#2C2418', fontWeight: 500 }}>{value}</p>
        {sub && <p style={{ margin: '2px 0 0', fontSize: 12, color: '#8B7D6B' }}>{sub}</p>}
      </div>
    </div>
  );
}

function TimelineItem({ year, text, color, editable, onEdit, onDelete }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', position: 'relative' }}>
      <div style={{
        position: 'absolute', left: -23, top: 4,
        width: 10, height: 10, borderRadius: '50%',
        background: color, border: '2px solid #FDFBF7',
      }} />
      <div style={{ flex: 1 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color }}>{year}</span>
        <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6B5E4F' }}>{text}</p>
      </div>
      {editable && (
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          <button onClick={onEdit} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#A0937F', padding: 3, borderRadius: 4,
          }}><Icons.Edit size={11} /></button>
          <button onClick={onDelete} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#C0392B', padding: 3, borderRadius: 4,
          }}><Icons.Trash size={11} /></button>
        </div>
      )}
    </div>
  );
}

function EventForm({ draft, onChange, onSave, onCancel }) {
  return (
    <div style={{
      background: '#FAF7F2', borderRadius: 10, border: '1px solid #EBE4D8',
      padding: 12, display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          type="text"
          value={draft.year}
          onChange={e => onChange(d => ({ ...d, year: e.target.value }))}
          placeholder="Year"
          style={{ ...miniInputStyle, width: 70 }}
        />
        <input
          type="text"
          value={draft.text}
          onChange={e => onChange(d => ({ ...d, text: e.target.value }))}
          placeholder="Describe this event…"
          style={{ ...miniInputStyle, flex: 1 }}
        />
      </div>
      {/* Color picker */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 11, color: '#8B7D6B', fontWeight: 600 }}>Color:</span>
        {EVENT_COLORS.map(c => (
          <button
            key={c.value}
            title={c.label}
            onClick={() => onChange(d => ({ ...d, color: c.value }))}
            style={{
              width: 18, height: 18, borderRadius: '50%', background: c.value,
              border: draft.color === c.value ? '2px solid #2C2418' : '2px solid transparent',
              cursor: 'pointer', padding: 0,
            }}
          />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={onCancel} style={{
          flex: 1, padding: '6px 0', borderRadius: 6, border: '1.5px solid #D5CAB8',
          background: '#fff', color: '#6B5E4F', fontSize: 12, fontWeight: 600,
          cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
        }}>Cancel</button>
        <button onClick={onSave} disabled={!draft.year.trim() || !draft.text.trim()} style={{
          flex: 2, padding: '6px 0', borderRadius: 6, border: 'none',
          background: !draft.year.trim() || !draft.text.trim() ? '#B8AFA0' : '#3B2F1E',
          color: '#F5EDE3', fontSize: 12, fontWeight: 600,
          cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
        }}>Save Event</button>
      </div>
    </div>
  );
}

function EditField({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder || ''}
        style={inputStyle}
      />
    </div>
  );
}

function RelationGroup({ label, ids, people }) {
  return (
    <div>
      <h4 style={sectionHeader}>{label}</h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {ids.map(id => {
          const p = people[id];
          if (!p) return null;
          return (
            <div key={id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
              background: '#FAF7F2', borderRadius: 10, border: '1px solid #EBE4D8',
            }}>
              <PersonAvatar person={p} size={36} borderColor="#DDD5C8" />
              <div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#2C2418' }}>{p.name}</p>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: '#A0937F' }}>
                  {p.birth?.slice(0, 4)}–{p.death ? p.death.slice(0, 4) : 'present'}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PersonAvatar({ person, size = 40, borderColor = '#DDD5C8' }) {
  const photoUrl = usePersonPhotoUrl(person, size);
  const fontSize = size <= 24 ? 9 : size <= 36 ? 12 : 13;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: person?.gender === 'F' ? '#E8D5C0' : '#C8CEDB',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize, fontWeight: 700, color: '#6B5E4F',
      fontFamily: "'Crimson Pro', Georgia, serif",
      border: `2px solid ${borderColor}`, overflow: 'hidden',
    }}>
      {photoUrl
        ? <img src={photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : getInitials(person?.name || '')
      }
    </div>
  );
}

function attachmentDisplay(type, name) {
  const ext = (name || '').split('.').pop().toLowerCase();
  // Extension takes priority so legacy Firestore data (type='pdf' for all docs) renders correctly
  if (['xlsx', 'xls', 'csv'].includes(ext))
    return { bg: '#DFF0E8', color: '#3A7A58', icon: <Icons.FileSpreadsheet size={16} /> };
  if (['mp3', 'wav', 'ogg', 'flac', 'm4a', 'aac', 'wma', 'opus', 'aif', 'aiff', 'amr', 'caf'].includes(ext) || type === 'audio')
    return { bg: '#EDE8F5', color: '#7B5EA7', icon: <Icons.Music size={16} /> };
  if (['mp4', 'mov', 'avi', 'mkv', 'webm', 'wmv', 'm4v'].includes(ext) || type === 'video')
    return { bg: '#E0EAF5', color: '#4A7FAB', icon: <Icons.Film size={16} /> };
  if (type === 'photo') return { bg: '#E3EBD8', color: '#7A9960', icon: null };
  return { bg: '#F0E6D8', color: '#A0876B', icon: <Icons.File size={16} /> };
}

function AttachmentRow({ a, canEdit, onDelete, visibility, canManageVisibility, members, isOwner, onSetVisibility }) {
  const [editingVisibility, setEditingVisibility] = useState(false);
  const [draft, setDraft] = useState(null);
  const [savingVisibility, setSavingVisibility] = useState(false);
  const signedUrl = useSignedUrl(a.storagePath || null);
  const url = signedUrl || a.url || null; // fall back to legacy token URL for existing attachments
  // The 40px preview and the View link want different objects: a rendition to
  // draw, the original to open. Passing signOriginal=false keeps this to the
  // single fetch it used to be for anything but an image — when there is no
  // rendition, the preview reuses the URL the View link already signed.
  const previewUrl = useImageUrl(a.storagePath || null, a.thumbs, a.thumbToken, 40, false) || url;
  const { bg, color, icon } = attachmentDisplay(a.type, a.name);
  // Audio that doesn't play on every device is converted in the background
  // after upload (api/audio.py). View is held back meanwhile: the file it
  // would open is the one an iPhone can't play, and the converted record
  // arrives through the attachment listener within a minute or so.
  const preparing = a.conversion === 'pending';

  const openVisibility = () => {
    setDraft(a.restricted ? (a.visibleTo || []).filter(uid => uid !== a.uploadedBy) : null);
    setEditingVisibility(true);
  };
  const saveVisibility = async () => {
    setSavingVisibility(true);
    try {
      await onSetVisibility(draft);
      setEditingVisibility(false);
    } catch (err) {
      alert(`Couldn't change who can see this file: ${err?.message || 'unknown error'}`);
    } finally {
      setSavingVisibility(false);
    }
  };

  return (
    <div style={{ background: '#FAF7F2', borderRadius: 10, border: '1px solid #EBE4D8' }}>
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 8, flexShrink: 0,
        background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
        color, overflow: 'hidden',
      }}>
        {a.type === 'photo' && previewUrl
          ? <img src={previewUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#2C2418', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</p>
        <p style={{ margin: '2px 0 0', fontSize: 11, color: '#A0937F', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span>
            {preparing ? 'Preparing for playback…'
              : a.conversion === 'failed' ? "Couldn't prepare for playback on all devices"
              : a.size ? `${(a.size / 1024).toFixed(0)} KB` : ''}
          </span>
          {visibility && (
            <span title={visibility.title} style={{
              display: 'inline-flex', alignItems: 'center', gap: 3,
              color: '#8A6530', fontWeight: 600,
            }}>
              <Icons.Lock size={10} /> {visibility.label}
            </span>
          )}
        </p>
      </div>
      {canManageVisibility && (
        <button onClick={editingVisibility ? () => setEditingVisibility(false) : openVisibility}
          title="Who can see this file" aria-label="Who can see this file"
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 4,
            color: a.restricted ? '#8A6530' : '#A0937F',
          }}>{a.restricted ? <Icons.Lock size={13} /> : <Icons.Users size={13} />}</button>
      )}
      {url && !preparing && (
        <a href={url} target="_blank" rel="noreferrer" style={{
          fontSize: 11, fontWeight: 600, color: '#C4975A', textDecoration: 'none',
          padding: '4px 8px', borderRadius: 6, background: 'rgba(196,151,90,0.1)',
        }}>View</a>
      )}
      {canEdit && (
        <button onClick={onDelete} style={{
          background: 'none', border: 'none', cursor: 'pointer', color: '#C0392B', padding: 4,
        }}><Icons.Trash size={12} /></button>
      )}
    </div>
    {editingVisibility && (
      <div style={{ padding: '0 14px 12px', borderTop: '1px solid #EBE4D8', paddingTop: 10 }}>
        <p style={{ ...labelStyle, marginBottom: 7 }}>Who can see this file</p>
        <VisibilityPicker value={draft} onChange={setDraft} members={members} isOwner={isOwner} compact />
        <p style={{ margin: '6px 0 0', fontSize: 11, color: '#A0937F' }}>
          Removing someone stops them opening it from now on. It can't take back a copy they already saved.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
          <button onClick={() => setEditingVisibility(false)} disabled={savingVisibility} style={{
            padding: '6px 12px', background: 'none', border: '1px solid #D5CAB8', borderRadius: 8,
            fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#6B5E4F', fontFamily: "'DM Sans', sans-serif",
          }}>Cancel</button>
          <button onClick={saveVisibility} disabled={savingVisibility} style={{
            padding: '6px 12px', background: '#C4975A', border: 'none', borderRadius: 8,
            fontSize: 12, fontWeight: 600, cursor: savingVisibility ? 'wait' : 'pointer', color: '#fff',
            fontFamily: "'DM Sans', sans-serif",
          }}>{savingVisibility ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    )}
    </div>
  );
}

const sectionHeader = {
  fontSize: 12, color: '#8B7D6B', textTransform: 'uppercase',
  letterSpacing: 1.2, margin: '0 0 12px', fontWeight: 700,
};

const labelStyle = {
  fontSize: 11, fontWeight: 600, color: '#6B5E4F',
  display: 'block', marginBottom: 5,
  textTransform: 'uppercase', letterSpacing: 0.8,
};

const inputStyle = {
  width: '100%', padding: '9px 12px', borderRadius: 8,
  border: '1.5px solid #D5CAB8', background: '#fff',
  fontSize: 13, color: '#2C2418', outline: 'none',
  fontFamily: "'DM Sans', sans-serif", boxSizing: 'border-box',
};

const miniInputStyle = {
  padding: '7px 10px', borderRadius: 6,
  border: '1.5px solid #D5CAB8', background: '#fff',
  fontSize: 12, color: '#2C2418', outline: 'none',
  fontFamily: "'DM Sans', sans-serif", boxSizing: 'border-box',
};
